import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AddIcon from "@material-ui/icons/Add";
import {
    Box,
    Button,
    Container,
    FormControl,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    Typography
} from '@deere/fuel-react';
import SearchIcon from "@material-ui/icons/Search";
import RatingProgramList from "./RatingProgramList";
import { RatingProgram } from "./RatingProgram";
import FeedbackStatusFilter from "../shared/filter/FeedbackStatusFilter";
import FunctionalAreaFilter from "../shared/filter/FunctionalAreaFilter";
import { checkedData, filterFunctionalAreas, searchWithFunctionalAreaList } from "../shared/utils";
import {useAuthConfig} from "../shared/helper";
import { useOktaAuth } from '@okta/okta-react';
import { Loading } from "../shared/Loading";
import FeedbackPeriodFilter from "../shared/filter/FeedbackPeriodFilter";

export const RatingPrograms = ({ name }) => {
    let authAxios = useAuthConfig();
    const { authState } = useOktaAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [ratingProgramList, setRatingProgramList] = useState(null);
    const [statusData] = useState([{ id: 1, description: 'Published' }, { id: 2, description: 'Unpublished' }]);
    const [statusFilterData, setStatusFilterData] = useState([]);
    const [statusExpanded, setStatusExpanded] = useState(true);
    const [campPeriodData, setCampPeriodData] = useState([]);
    const [campPeriodFilterData, setCampPeriodFilterData] = useState([]);
    const [periodExpanded, setPeriodExpanded] = useState(false);
    const [functionalAreaData, setFunctionalAreaData] = useState([]);
    const [functionalAreaFilterData, setFunctionalAreaFilterData] = useState([]);
    const [categoryExpanded, setCategoryExpanded] = useState(false);
    const [searchParam] = useState(["fiscalYear","resStatus"]);
    const [searchParamDesc] = useState(["functionalAreas", "subFunctionalAreas"]);
    const [query, setQuery] = useState("");

    const getRatingProgramComponent = (list) => {
        return list.map((item) => {
            return (
                <RatingProgram ratingProgram={item} key={item.id} />
            );
        });
    }

    const toggleStatusFilter = () => {
        setStatusExpanded(!statusExpanded);
    };

    const togglePeriodFilter = () => {
        setPeriodExpanded(!periodExpanded);
    };

    const toggleFunctionalAreaFilter = () => {
        setCategoryExpanded(!categoryExpanded);
    };

    const handleUpdatedStatus = data => {
        const arr = checkedData(data)
        setStatusFilterData(arr);
    }

    const handleUpdatedPeriod = data => {
        const arr = checkedData(data)
        setCampPeriodFilterData(arr);
    }

    const handleUpdatedFunctionalArea = data => {
        const arr = checkedData(data)
        setFunctionalAreaFilterData(arr);
    }

    const getRatingProgramList = async () => {
        setIsLoading(true)
        try {
            return await authAxios.get(`/rating-program`);
        } catch (e) {
            console.error(e);
        }
    };

    const processRatingProgam = (response) => {
        const updatedData = response.map((row) => {
            const resStatus = { resStatus: `${row.publishStatus}` === 'N' ? "Unpublished" : "Published" }
            row = { ...resStatus, ...row };
            return row;
        });
        const uniqueCampPeriod = [...new Map(updatedData.map(item =>
            [item["fiscalYear"], item])).values()]

        const uniqueStatusIds = [...new Set(updatedData.map(item => item.resStatus))];
        const uniquePeriodIds = [...new Set(updatedData.map(item => item.fiscalYear))];
        const functionalAreas = updatedData.map(item => {
            return (item.functionalAreas.map(functionalArea => functionalArea.description))
        });
        const uniqueFunctionalAreas = ([...new Set([].concat(...functionalAreas))])

        setStatusFilterData(uniqueStatusIds);
        setCampPeriodData(uniqueCampPeriod)
        setCampPeriodFilterData(uniquePeriodIds)
        setFunctionalAreaData(uniqueFunctionalAreas)
        setFunctionalAreaFilterData(uniqueFunctionalAreas)
        setIsLoading(false);
        const list = getRatingProgramComponent(updatedData);
        setRatingProgramList(list)
    };

    const filterRatingProgramsList = (items) => {
        return items && items.filter((item) => {
            const tempFunctionalArea = filterFunctionalAreas(item.props.ratingProgram.functionalAreas, functionalAreaFilterData)
            return statusFilterData.includes(item.props.ratingProgram.resStatus)
                && tempFunctionalArea.length > 0
                && campPeriodFilterData.includes(item.props.ratingProgram.fiscalYear)
                && searchWithFunctionalAreaList(item.props.ratingProgram, searchParam, searchParamDesc, query)
        })
    }

    useEffect(() => {
        getRatingProgramList().then(res => {
            if (res)
                processRatingProgam(res.data)
        });

        return () => {
            setRatingProgramList(null);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState]);

    return (
        <React.Fragment>
            <Grid container>
                <Grid item xs={3}>
                    <Typography variant="h1" component="h1" data-testid="PageName">
                        {name}
                    </Typography>
                </Grid>
                <Grid item xs={9}>
                    <Container>
                        <Box display="flex" justifyContent="flex-end">
                            <Link
                                to={{
                                    pathname: "/rating-programs/create-rating-program",
                                }}
                                style={{ textDecoration: "none" }}
                            >
                                <Button variant="primary" startIcon={<AddIcon />}>
                                    Create New
                                </Button>
                            </Link>
                        </Box>
                    </Container>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={12} md={3}>
                    <Box mt={4}>
                        <Button variant="text-inline" size="large">Clear All</Button>
                    </Box>
                    <Box mt={1}>
                        <div className="jdsn-facet-container">
                            <FeedbackStatusFilter
                                statusData={statusData}
                                filteredData={statusFilterData}
                                onExpandClick={toggleStatusFilter}
                                onStatusUpdate={handleUpdatedStatus}
                                expanded={statusExpanded}
                                name={"Status"} />

                            <FunctionalAreaFilter
                                statusData={functionalAreaData}
                                filteredData={functionalAreaFilterData}
                                onExpandClick={toggleFunctionalAreaFilter}
                                onStatusUpdate={handleUpdatedFunctionalArea}
                                expanded={categoryExpanded}
                                name={"Functional Area"} />

                            <FeedbackPeriodFilter
                                periodData={campPeriodData}
                                filteredData={campPeriodFilterData}
                                onExpandClick={togglePeriodFilter}
                                onPeriodUpdate={handleUpdatedPeriod}
                                expanded={periodExpanded}
                                name={"Ratings"}
                            />

                            <Box className="fixed-bottom position-sticky view-result">
                                <Button variant="primary" fullWidth={true} size="large">View Results</Button>
                            </Box>
                        </div>
                    </Box>
                </Grid>
                <Grid item xs={12} md={9}>
                    <Container>
                        <Box mt={4}>
                            <FormControl fullWidth>
                                <Input fullWidth placeholder="Filter these results" id="search"
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton aria-label="search">
                                                <SearchIcon style={{ color: " #367c2b" }} />
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    onChange={(e) => setQuery(e.target.value)} />
                            </FormControl>
                        </Box>
                        <Box mt={4}>
                            {isLoading && (
                                <Loading />
                            )}
                            {!isLoading && (
                                <RatingProgramList list={filterRatingProgramsList(ratingProgramList)} />
                            )}
                        </Box>
                    </Container>
                </Grid>
            </Grid>
        </React.Fragment>
    );
}
